// entry module for the v3 cart drawer — everything else is pulled in from here
// cart-upsell.js -> cart-state.js -> cart-api.js + cart-group-engine.js + cart-rules.js
// cart-drawer-rewards.js -> cart-renderer.js + cart-rules.js
// cart-drawer-giftcard-editor.js -> cart-rules.js
// cart-drawer-gift.js -> cart-renderer.js + cart-drawer-rewards.js
// cart-header-badge.js -> nothing; a document listener on cart:updated for the
//                         SITE header's bag count (outside this element)
import 'cart-upsell';
import 'cart-header-badge';
import { hydrateFromDOM, hydrate, subscribe, actions, getState } from 'cart-state';
import { renderCart, formatMoney, formatMoneyHtml } from 'cart-renderer';
import { syncWishlistMirror, toggleWishlist, observeWishlistState } from 'cart-wishlist-mirror';
import { buildRewardsView, paintRewards, pickableGift } from 'cart-drawer-rewards';
import { getProduct } from 'cart-api';
import { CartGiftcardEditor } from 'cart-drawer-giftcard-editor';
import { CartGiftPicker } from 'cart-drawer-gift';

// gwp-app.js flags completion with window.theme.gwp_status only — no event —
// so the drawer polls it (below). Long enough for its product fetches on a
// slow connection; after this the gift tiers wait for the next cart mutation.
const GWP_LOAD_TIMEOUT_MS = 10000;
const GWP_LOAD_POLL_MS = 200;

// The busy overlay (setBusy): the watchdog that takes it down whatever became
// of the request, and the linger that bridges two back-to-back commits. The
// watchdog sits above cart-api.js's REQUEST_TIMEOUT_MS (8s) on purpose — the
// abort there is what normally ends a dead request, through the ordinary error
// path; this only catches one that escaped that signal.
const BUSY_TIMEOUT_MS = 10000;
const BUSY_LINGER_MS = 150;

// The phone breakpoint, the same query cart-drawer.css's mobile block uses.
// Only placeUpsell() reads it: where the upsell element lives is the one
// layout decision CSS cannot make, because it has to change parents.
const MOBILE_QUERY = '(max-width: 990px)';

class ThemeCartDrawer extends HTMLElement {

  connectedCallback() {

    this.dialog =
      this.querySelector('.cart-drawer-modal__content');

    if (!this.dialog) return;

    // the overlay's own state, for setBusy(); the dialog ships aria-busy="false"
    this.busy = false;

    // Subscribe BEFORE hydrating: hydrate() notifies synchronously, so a
    // subscription made after it would miss the first (and only) paint.
    this.initRenderer();
    this.initGiftcardEditor();
    this.initGiftPicker();
    this.initUpsellPlacement();
    this.hydrate();
    this.bindEvents();
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.mobileQuery) listenMedia(this.mobileQuery, this.placeUpsell, false);
    if (this.wishlistObserver) this.wishlistObserver.disconnect();
    if (this.gwpPollTimer) clearInterval(this.gwpPollTimer);
    if (this.busyTimer) clearTimeout(this.busyTimer);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    document.removeEventListener('click', this.handleClick);
    document.removeEventListener('cart:gift-added', this.handleGiftAdded);
  }

  initRenderer() {
    // The list shell is always rendered (cart-drawer.liquid ships both the
    // filled and the empty state; data-cart-empty picks the visible one), so
    // this only fails on a shell that is not the drawer's at all.
    const root = this.querySelector('[data-cart-blocks-root]');
    this.blocksContainer = root && root.querySelector('[data-blocks-container]');
    if (!this.blocksContainer) return;
    this.listRoot = root;

    let i18n = {};
    const i18nNode = root.querySelector('[data-cart-i18n]');
    if (i18nNode) {
      try {
        i18n = JSON.parse(i18nNode.textContent);
      } catch (error) {
        console.warn('[cart-drawer] bad data-cart-i18n JSON', error);
      }
    }

    this.renderCtx = {
      moneyFormat: root.dataset.moneyFormat || '${{amount}}',
      currencySymbol: root.dataset.currencySymbol || '$',
      i18n,
      // Promo callouts ("2 for $20") carry the presentment currency and go
      // through the theme's currency switcher, exactly as the PLP cards do.
      // Both live on window, so they enter the (window-free) renderer here.
      // The renderer hands over html-escaped text and inserts the return
      // value as HTML; getCurrencyHTML wraps the converted amount in markup.
      // A throwing switcher (slate not ready, odd money format) must not take
      // the whole paint down with it, so it degrades to the plain text.
      currency: (window.theme && window.theme.currentCurrency) || '',
      formatPromoText: (text) => {
        const switcher = window.theme && window.theme.CurrencySwitcher;
        if (!switcher || typeof switcher.getCurrencyHTML !== 'function') return text;
        try {
          return switcher.getCurrencyHTML(text);
        } catch (error) {
          console.warn('[cart-drawer] promo currency conversion failed', error);
          return text;
        }
      },
    };

    // Rewards strip (cart-drawer-rewards.liquid). Liquid painted the first
    // frame; from here JS owns its attributes. Its strings sit in their own
    // JSON node, so it gets its own ctx. Rendered alongside the list shell, so
    // it is absent exactly when blocksContainer is.
    this.rewardsRoot = this.querySelector('[data-cart-rewards]');
    this.rewardsCtx = {
      moneyFormat: this.renderCtx.moneyFormat,
      i18n: parseJsonNode(
        this.rewardsRoot && this.rewardsRoot.querySelector('[data-rewards-i18n]'),
        'data-rewards-i18n'
      ),
    };

    this.unsubscribe = subscribe((state) => this.render(state));

    // Swym paints its wishlist state into the hidden mirror table; this
    // pushes it back onto the hearts whenever that table changes.
    this.wishlistObserver = observeWishlistState(this);
  }

  /**
   * Where the upsell lives. cart-drawer.liquid renders <cart-upsell> as a child
   * of the dialog, next to .cart-drawer__main: on desktop that is the side
   * panel, and in the empty state on mobile it is the block under the category
   * tiles. With items in the bag on mobile the design wants it at the END of
   * the item list, scrolling with the items, 40px under the last block — and
   * the list is its own scroller (.cart-drawer__line-items), so the element
   * has to move inside it. CSS cannot reparent, so this does: the element is
   * moved, never cloned, so there is one <cart-upsell> and one set of picker
   * ids. Moving it runs cart-upsell.js's disconnected/connected pair, which
   * re-binds its listeners and re-subscribes; the `hidden` flags on its cards
   * are attributes, so nothing visible resets. Re-evaluated on every paint
   * (the empty flip) and on the breakpoint change; a no-op when the element
   * already sits where it belongs. The mobile rules in cart-drawer.css show it
   * only inside the list or in the empty state, so until this runs the first
   * paint is the Liquid one, as before.
   */
  initUpsellPlacement() {
    this.upsell = this.querySelector('[data-cart-upsell]');
    this.mainPanel = this.dialog.querySelector('.cart-drawer__main');
    if (!this.upsell || !this.mainPanel || !this.listRoot) return;

    this.mobileQuery = window.matchMedia(MOBILE_QUERY);
    listenMedia(this.mobileQuery, this.placeUpsell, true);
    this.placeUpsell();
  }

  placeUpsell = () => {
    if (!this.upsell || !this.mobileQuery) return;

    const inList = this.mobileQuery.matches && !this.hasAttribute('data-cart-empty');
    if (inList) {
      if (this.upsell.previousElementSibling !== this.blocksContainer) this.blocksContainer.after(this.upsell);
    } else if (this.upsell.previousElementSibling !== this.mainPanel) {
      this.mainPanel.after(this.upsell);
    }
  }

  /**
   * Second dialog in the shell — a SIBLING of the drawer's own, never a child
   * of it (cart-drawer.liquid). The editor is its own component
   * (cart-drawer-giftcard-editor.js); the drawer only hands it the store, the
   * product fetch, the money format, the shared busy lock and a way to close
   * the whole stack, then calls open/close/prefetch. After initRenderer(): it
   * reads renderCtx.moneyFormat.
   */
  initGiftcardEditor() {
    const dialog = this.querySelector('[data-giftcard-editor]');
    if (!dialog) return;

    this.giftcardEditor = new CartGiftcardEditor(dialog, {
      getState,
      actions,
      getProduct,
      formatMoney,
      moneyFormat: this.renderCtx && this.renderCtx.moneyFormat,
      // One lock for stepper and editor alike — see setPending().
      isPending: () => Boolean(this.pending),
      setPending: (pending) => this.setPending(pending),
      // A press outside the editor dismisses everything, not just the editor:
      // close() tears the editor down first, then slides the drawer out.
      closeAll: () => this.close(),
    });
  }

  initGiftPicker() {
    const dialog = this.querySelector('[data-gift-picker]');
    if (!dialog) return;

    this.giftPicker = new CartGiftPicker(dialog, {
      getState,
      actions,
      i18n: parseJsonNode(dialog.querySelector('[data-gift-i18n]'), 'data-gift-i18n'),
      isPending: () => Boolean(this.pending),
      setPending: (pending) => this.setPending(pending),
    });
  }

  /**
   * Every state notification lands here. Two jobs: decide which of the two
   * shells is visible, then paint into the filled one.
   *
   * data-cart-empty (cart-drawer.liquid stamps the first frame, CSS hides the
   * other shell) flips when a mutation crosses zero — an upsell add into an
   * empty cart, or removing the last line. That flip alone is wrapped in a
   * View Transition, so the empty tiles cross-fade into the list rather than
   * snapping; .cart-drawer__main carries the view-transition-name, keeping the
   * effect inside the main panel. Everything the paint does — blocks, total,
   * count badge, rewards, Swym mirror — runs inside the same callback, so a
   * flipped frame is never half painted — and so does the upsell's move into or
   * out of the list (placeUpsell), which follows the attribute.
   *
   * Two guards on the flip:
   *   - the 'loading' notification carries the PRE-mutation cart (cart-state.js
   *     commit), so item_count is unchanged there and nothing flips early;
   *   - on the actions.load() fallback path the first notification has
   *     state.cart === {} — no items array — and the attribute is left as
   *     Liquid stamped it rather than judged off a cart that is not there yet.
   *
   * startViewTransition runs its callback a frame later. A second notification
   * inside that frame starts a new transition and skips the old one; both
   * callbacks still run, in order, so no paint is lost. No native support (or
   * a reduced-motion preference, which the CSS turns into a zero-length
   * transition) degrades to the direct paint.
   */
  render(state) {
    // The 'loading' notification (cart-state.js commit) carries the
    // PRE-mutation cart: painting it reproduces what is on screen, and on iOS
    // Safari that second full innerHTML swap per tap re-decoded every image
    // and flashed the list. Skip it once something has been painted; the
    // pending lock is setPending()'s job, not this paint's. The very first
    // notification still paints so a drawer booted via actions.load() is not
    // left empty.
    //
    // The busy overlay follows the status, that skipped 'loading' included —
    // which is why setBusy() runs before the early return.
    this.setBusy(state.status === 'loading');

    if (state.status === 'loading' && this.painted) return;

    const hasCart = Boolean(state.cart) && Array.isArray(state.cart.items);
    const wasEmpty = this.hasAttribute('data-cart-empty');
    const isEmpty = hasCart ? state.cart.item_count === 0 : wasEmpty;
    const flipped = wasEmpty !== isEmpty;

    // Decided against the set the PREVIOUS paint recorded, so before this one
    // overwrites it — see hasNewProduct() / scrollListToTop().
    const newProduct = this.hasNewProduct(state);

    const paint = () => {
      this.toggleAttribute('data-cart-empty', isEmpty);
      this.placeUpsell();
      this.paintContents(state);
      if (flipped && isEmpty) this.focusEmptyState();
      if (newProduct) this.scrollListToTop();
    };

    if (flipped && typeof document.startViewTransition === 'function') {
      document.startViewTransition(paint);
    } else {
      paint();
    }
  }

  /**
   * The paint proper — the list plus every Liquid-rendered number and strip
   * the state drives. Called from render() only, inside the transition when
   * there is one.
   */
  paintContents(state) {
    // innerHTML replaces the whole list, so the button that was just pressed
    // stops existing mid-interaction. Remember it by line key + action (the two
    // attributes the renderer stamps) and put the caret back afterwards,
    // otherwise every step drops a keyboard user at the top of the drawer.
    const active = document.activeElement;
    const focused = active && this.blocksContainer.contains(active) && active.dataset.action
      ? { action: active.dataset.action, key: active.dataset.lineKey }
      : null;

    this.blocksContainer.innerHTML = renderCart(state, this.renderCtx);
    this.painted = true;

    // What this paint shows, for the next paint's hasNewProduct(). Only a real
    // cart is a baseline: the actions.load() boot path paints once with
    // state.cart === {} and must not make every product "new" a moment later.
    if (state.cart && Array.isArray(state.cart.items)) {
      this.paintedProducts = new Set(
        (state.flatItems || [])
          .filter((line) => !line.isGwp && line.product_id != null)
          .map((line) => line.product_id)
      );
    }

    if (focused) {
      const restored = this.blocksContainer.querySelector(
        `[data-action="${focused.action}"][data-line-key="${focused.key}"]`
      );
      if (restored) restored.focus();
    }

    // Hidden Swym mirror table (cart-drawer-line-items-list.liquid) follows
    // the item set so the wishlist app has an anchor for every line.
    syncWishlistMirror(this, state);

    // The summary itself is Liquid-rendered; only the number is live.
    // innerHTML: the symbol is markup — <em class="currency-symbol"> + the
    // number, the drawer's price layout (formatMoneyHtml). Same symbol source
    // as the line items (data-currency-symbol) and the same split as the
    // first paint in cart-drawer-summary.liquid.
    const total = this.querySelector('[data-cart-total]');
    if (total && state.cart) {
      total.innerHTML = formatMoneyHtml(
        state.cart.total_price,
        this.renderCtx.moneyFormat,
        this.renderCtx.currencySymbol
      );
    }

    // Same deal for the drawer's own header count: Liquid paints it, only the
    // number is live. The SITE header's bag badge is outside this element —
    // cart-header-badge.js follows cart:updated for that.
    const count = this.querySelector('[data-cart-count]');
    if (count && state.cart) {
      count.textContent = state.cart.item_count;
    }

    this.paintRewards(state);
  }

  /**
   * The rewards strip: free-shipping sentence, tier bar, select-gift button.
   * Also called from open(), on purpose — buildRewardsView re-reads the geo
   * blocklist live, and sessionStorage 'country' (regionalisation.liquid,
   * geoip2 callback) may not have existed yet when the page hydrated.
   */
  paintRewards(state) {
    if (!this.rewardsRoot) return;
    paintRewards(this.rewardsRoot, buildRewardsView(state), this.rewardsCtx);
  }

  /**
   * Does this state hold a product the last paint did not? Product, not line
   * key: a quantity change can re-split a line against a promotion and hand
   * it a fresh key, and that must not read as an add. Gift lines are left
   * out — their block sorts after every other line (buildGiftBlocks), so a
   * gift arriving is no reason to go to the top. A line without a product_id
   * is ignored rather than counted as new, and the first paint has nothing to
   * compare against: both answer no, never a stray scroll.
   */
  hasNewProduct(state) {
    if (!this.paintedProducts) return false;
    return (state.flatItems || []).some(
      (line) => !line.isGwp && line.product_id != null && !this.paintedProducts.has(line.product_id)
    );
  }

  /**
   * Back to the top of the list after a paint that brought a NEW product in —
   * the one moment the shopper needs taking somewhere. Shopify lists the
   * newest line first, and both ways a line arrives leave the list scrolled
   * away from it: an upsell add (on phones the upsell sits at the END of the
   * list, so the shopper is at the bottom by definition), and an add made
   * while the drawer was closed — PDP, PLP — which shows up when the open-time
   * refresh() lands. Quantity changes, removals and the gift card editor's
   * remove + re-add never come here (hasNewProduct), nor do gifts.
   *
   * Deferred a frame on purpose. The upsell's own paint (syncVisibility,
   * cart-upsell.js) hands focus to the neighbouring card when the added one
   * hides, and focus() scrolls that card into view — on phones, the bottom of
   * the list. Whether it runs before or after this paint depends on
   * subscription order, which placeUpsell() changes whenever it moves the
   * element, so the scroll waits for the whole notification to finish and
   * has the last word.
   *
   * Only a scrolled container moves. Chrome and Safari drop the offset with
   * display:none, so a reopen there is already at the top and nothing
   * happens; Firefox keeps it, and there the open-time case is what moves the
   * list, after the drawer has landed. The dialog is the scroller in the
   * empty state on phones; a flip out of it leaves it unscrollable, and a
   * stale offset there costs nothing to clear. Smooth, unless the shopper
   * asked for less motion; scrollTop is the fallback where scrollTo is
   * missing (jsdom, old engines).
   */
  scrollListToTop() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      const reduced = typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = reduced ? 'auto' : 'smooth';
      [this.listRoot, this.dialog].forEach((scroller) => {
        if (!scroller || !(scroller.scrollTop > 0)) return;
        if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top: 0, behavior });
        else scroller.scrollTop = 0;
      });
    });
  }

  /**
   * The list just emptied under the shopper's focus: the stepper they pressed
   * is gone and the whole list shell is now display:none, so the focus-restore
   * in paintContents() finds nothing and focus would drop to <body> — inside a
   * modal dialog that strands a keyboard user. Land on the empty state's CTA
   * (cart-drawer-empty.liquid), or on the close button when the CTA is absent.
   */
  focusEmptyState() {
    const target =
      this.querySelector('.cart-drawer-empty__no-items a.btn') ||
      this.querySelector('[data-cart-drawer-close]');
    if (target) target.focus();
  }

  /**
   * First paint costs NOTHING on the network: layout/theme.liquid already
   * rendered the same `cart?view=json` payload into #cart-bootstrap, and module
   * scripts are deferred so that node is in the DOM by the time we run.
   *
   * The fetch is only a fallback for when the node is absent — a page served
   * from cache before the bootstrap existed, or a drawer mounted dynamically.
   * It goes to `cart?view=json`, never `/cart.js`: the Shopify shape has no
   * `max` / `inventory_*`, `properties._collections`, `position` or
   * `discount_allocations`, so hydrating from it would come up with no stock,
   * no stepper ceiling and no bundles.
   */
  hydrate() {
    if (hydrateFromDOM()) {
      this.rehydrateWhenGwpLoads();
      return;
    }

    actions.load()
      .then(() => this.rehydrateWhenGwpLoads())
      .catch((error) => {
        console.warn('[cart-drawer] could not load the cart', error);
      });
  }

  /**
   * gwp-app.js fills window.theme.giftWithPurchase asynchronously (one product
   * fetch per gift) and marks completion with gwp_status = 'loaded' — there is
   * no event. It runs before this module (defer, earlier in theme.liquid) so
   * the flag reads 'loading' here whenever GWP is configured; if the cart
   * hydrated in that window, extractGifts saw no offers and the gift segments
   * would only appear after the next cart mutation. Poll the flag, as the
   * legacy cart did (side-cart.js waitForGwpStatusAndRun), then rebuild the
   * view from the cart already in state — no network round-trip.
   */
  rehydrateWhenGwpLoads() {
    if (!window.theme || window.theme.gwp_status !== 'loading') return;
    if (this.gwpPollTimer) clearInterval(this.gwpPollTimer);

    const started = Date.now();
    this.gwpPollTimer = setInterval(() => {
      const status = window.theme && window.theme.gwp_status;
      if (status === 'loading' && Date.now() - started < GWP_LOAD_TIMEOUT_MS) return;

      clearInterval(this.gwpPollTimer);
      this.gwpPollTimer = null;

      if (status === 'loaded') hydrate(getState().cart);
    }, GWP_LOAD_POLL_MS);
  }

  bindEvents() {

    document.addEventListener(
      'click',
      this.handleClick
    );

    // Esc on a modal dialog closes it natively and instantly. Route it through
    // close() instead so it slides out like every other dismissal.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      // Esc unwinds one level at a time: an open upsell size picker goes
      // first, the drawer second. cart-upsell.js already swallows the Escape
      // keydown itself; this is the fallback for an engine that raises the
      // close request regardless.
      const upsell = this.querySelector('cart-upsell');
      if (upsell && typeof upsell.closePicker === 'function' && upsell.closePicker()) return;
      this.close();
    });

    // Safety net for any path that closes the dialog without going through
    // close() — the drawer must never leave --visible or the body scroll lock
    // behind. Idempotent, so the normal close() flow lands here harmlessly.
    this.dialog.addEventListener('close', () => {
      this.cancelPendingClose();
      this.classList.remove('cart-drawer-modal--visible');
      document.body.classList.remove('drawer-open');
    });

    // Light dismiss: a press on the backdrop closes the drawer, like the X.
    // The backdrop has no element of its own — the event lands on the dialog —
    // so handleBackdropPress tells it apart by geometry (a point outside the
    // dialog's box), see there. The editor is a separate top-layer dialog and
    // takes its own presses (cart-drawer-giftcard-editor.js); while it is open
    // the drawer is inert and nothing arrives here — a press outside the
    // editor reaches close() through its closeAll dep instead
    // (initGiftcardEditor) and takes the whole stack down.
    this.dialog.addEventListener('pointerdown', this.handleBackdropPress);

    // Scoped to the list rather than the document: the container node itself
    // survives every repaint (only its innerHTML is swapped), so one delegated
    // listener covers all present and future steppers — and the my-bag page,
    // which drives its own Vue stepper off the same classes, never reaches this.
    if (this.blocksContainer) {
      this.blocksContainer.addEventListener('click', this.handleQtyClick);
      this.blocksContainer.addEventListener('click', this.handleGiftcardEditClick);
      this.blocksContainer.addEventListener('click', this.handleGiftcardRemoveClick);
      this.blocksContainer.addEventListener('click', this.handleWishlistClick);
      this.blocksContainer.addEventListener('click', this.handleGiftOpenClick);
    }

    // The gift CTA sits in the rewards strip, outside blocksContainer, so it
    // needs its own scope; that node survives repaints the same way.
    if (this.rewardsRoot) {
      this.rewardsRoot.addEventListener('click', this.handleSelectGiftClick);
    }

    document.addEventListener('cart:gift-added', this.handleGiftAdded);
  }

  handleQtyClick = (event) => {
    const button = event.target.closest('[data-action="qty-plus"], [data-action="qty-minus"]');
    if (!button) return;

    this.changeQuantity(
      button.dataset.lineKey,
      button.dataset.action === 'qty-plus' ? 1 : -1
    );
  }

  /**
   * The heart is a proxy: the real wishlist button is the one Swym injected
   * into the hidden mirror table for this product (cart-wishlist-mirror.js).
   * Its pressed state comes back through the mirror observer, not from here.
   */
  handleWishlistClick = (event) => {
    const button = event.target.closest('[data-action="wishlist"]');
    if (!button) return;

    toggleWishlist(this, button.dataset.handle);
  }

  /**
   * Only Digital Gift Card lines render this button (cart-renderer.js), so the
   * delegated listener needs no product check of its own. The button lives in
   * the drawer's list, so the delegation stays here; everything after the
   * click belongs to the editor.
   */
  handleGiftcardEditClick = (event) => {
    const button = event.target.closest('[data-action="giftcard-edit"]');
    if (!button || !this.giftcardEditor) return;

    this.giftcardEditor.open(button.dataset.lineKey);
  }

  handleGiftOpenClick = (event) => {
    const button = event.target.closest('[data-action="gift-open"]');
    if (!button || !this.giftPicker) return;

    this.giftPicker.open(button.dataset.gwpBlockId);
  }

  // The rewards CTA names no offer: it opens the one its own visibility rule found.
  handleSelectGiftClick = (event) => {
    if (!event.target.closest('[data-action="select-gift"]') || !this.giftPicker) return;

    const gift = pickableGift(getState().gifts);
    if (gift) this.giftPicker.open(gift.gwpBlockId);
  }

  // GA hook for gift adds, auto and picked alike (cart-state.js dispatches it).
  // Same payload the legacy carts pushed (side-cart.js:934).
  handleGiftAdded = (event) => {
    const { product } = event.detail || {};
    const variant = product && product.variants && product.variants[0];
    if (!variant) return;

    const payload = {
      event: 'cartOffersAddToCart',
      productTitle: `${variant.sku} - ${product.title}`,
      value: Number(variant.price) / 100,
    };
    if (typeof window.pushEvent === 'function') window.pushEvent(payload);
    else (window.dataLayer = window.dataLayer || []).push(payload);
  }

  /**
   * The gift card's bin deletes the whole line in one press, whatever its
   * quantity. Only Digital Gift Card lines render this action
   * (cart-renderer.js); the stepper's qty-minus is untouched.
   */
  handleGiftcardRemoveClick = (event) => {
    const button = event.target.closest('[data-action="giftcard-remove"]');
    if (!button) return;

    this.removeLine(button.dataset.lineKey);
  }

  /**
   * The stepper edits the LINE, never the slice it was clicked in.
   *
   * A qty-4 line against a "3 for X" offer renders twice — a slice of 3 in the
   * bundle block, a slice of 1 in the pending block — and both steppers carry
   * the SAME data-line-key. Reading the new quantity off the clicked slice would
   * send 4 from one row and 2 from the other, so the real quantity comes from
   * flatItems and the click only supplies the direction.
   *
   * One change.js call serves every block type. The Vue cart had to send add.js
   * for a multibuy increase (cart.js:4228) because a grouped row could not name
   * the real line behind it; slice.lineKey names it outright.
   */
  changeQuantity(key, delta) {
    if (!key || this.pending) return;

    const line = (getState().flatItems || []).find((item) => item.key === key);
    if (!line) return;

    const next = Number(line.quantity) + delta;

    // Zero IS the deletion: change.js removes a line when its quantity reaches
    // 0, which is how the my-bag cart deletes too (removeItem, cart.js:2552).
    // update.js is not needed — that endpoint only earns its keep for BATCH
    // removal, several keys zeroed in one request (removeItems, cart.js:2487).
    //
    // Nothing branches on "is this a removal" because `next` already carries the
    // answer: the count comes off the LINE, so the bin on a bundle-split row
    // reading 1 whose line holds 4 lands here as 4 -> 3 and removes nothing,
    // while a genuine last unit lands as 1 -> 0 and goes. Negative is
    // unreachable (quantity >= 1, delta -1); the guard is belt and braces.
    if (next < 0) return;

    // Stock ceiling — line.stockCeiling, the quantity change.js would still
    // accept (cart-group-engine.js stockCeilingOf: Shopify's raw count for a
    // tracked, deny-oversell variant less the variant's other lines, no badge
    // buffer, never `max`; null when nothing caps it). The renderer already
    // ships the plus `disabled` at the ceiling (cart-renderer.js atMax), so a
    // press normally never lands here; this is the backstop for a stale paint
    // or a synthetic click, and it must stay the same test. 0 or less is a
    // ceiling already reached, not an unknown.
    const ceiling = line.stockCeiling;
    if (delta > 0 && ceiling !== null && ceiling !== undefined && next > ceiling) return;

    this.setPending(true);

    // Removing the last line needs nothing extra here: the state notification
    // reaches render(), which sees item_count cross to zero and flips the
    // drawer to its empty shell (cart-drawer.liquid ships both). This used to
    // window.location.reload() so Liquid could pick the empty state.
    actions
      .change({ id: key, quantity: next })
      .catch((error) => console.warn('[cart-drawer] quantity change failed', error))
      .finally(() => this.setPending(false));
  }

  /**
   * Zero is the deletion, as in changeQuantity(), but sent outright instead of
   * line.quantity - 1. Shares the same pending lock, so it cannot race a
   * stepper press or the gift card editor's remove + re-add.
   */
  removeLine(key) {
    if (!key || this.pending) return;

    this.setPending(true);
    actions
      .change({ id: key, quantity: 0 })
      .catch((error) => console.warn('[cart-drawer] remove failed', error))
      .finally(() => this.setPending(false));
  }

  /**
   * Every mutation is change.js + a `cart?view=json` re-read + a full repaint, so
   * a second click mid-flight would compute its delta from the pre-request
   * quantity and land on the wrong number. The gift card editor's confirm
   * (remove + re-add) shares this same lock through the callbacks handed to it
   * in initGiftcardEditor(). The attribute itself is still unstyled: the busy
   * overlay keys off the store's status instead (setBusy), so it also covers
   * the requests that never take this lock — upsell adds, the GWP sync.
   */
  setPending(pending) {
    this.pending = pending;
    this.blocksContainer.toggleAttribute('data-cart-pending', pending);
  }

  /**
   * The busy overlay (.cart-drawer__busy in cart-drawer.liquid) over the main
   * column while a cart request is in flight — ANY cart request: the stepper
   * and the bin here, the editor's and the picker's confirms, an upsell add,
   * the refresh on open and the GWP sync all go through cart-state's commit(),
   * so the store's status is the one signal that sees them all, where the
   * pending lock only knows the mutations that take it. render() calls this
   * on every notification, ahead of its own 'loading' early return.
   *
   * data-cart-busy on the host drives the CSS (shown 200ms in, hidden 200ms
   * out — the legacy toggleLoaderOverlay timings); aria-busy on the dialog is
   * the accessible side of the same state.
   *
   * One timer slot, two jobs, since the states are exclusive:
   *   - linger on the way OUT. The editor's confirm and the gift sync are two
   *     commits back to back (remove, then add) and the store reads 'idle' in
   *     between; holding the overlay for BUSY_LINGER_MS keeps it from dipping.
   *   - watchdog on the way IN. Whatever becomes of the request, the overlay
   *     comes down after BUSY_TIMEOUT_MS. cart-api.js aborts every cart
   *     request at 8s, which releases the lock and the status through the
   *     normal error path; this only fires for a request that escaped that.
   */
  setBusy(busy) {
    if (busy === this.busy) return;
    this.busy = busy;
    clearTimeout(this.busyTimer);

    if (busy) {
      this.toggleAttribute('data-cart-busy', true);
      this.dialog.setAttribute('aria-busy', 'true');
      this.busyTimer = setTimeout(() => this.setBusy(false), BUSY_TIMEOUT_MS);
      return;
    }

    this.busyTimer = setTimeout(() => {
      this.removeAttribute('data-cart-busy');
      this.dialog.setAttribute('aria-busy', 'false');
    }, BUSY_LINGER_MS);
  }

  /**
   * Re-read the cart on open.
   *
   * Adds made outside the drawer leave the store behind: the PLP quick add
   * (product-card.js) still posts to /cart/add.js through the legacy
   * theme.sideCart and never touches cart-state, so a drawer opened after it
   * would paint the cart as it was at page load. There is no reliable signal
   * to hook instead — side-cart's events go away with side-cart itself — so
   * the open itself is the trigger: one `cart?view=json` read per open, at
   * the only moment the list has to be right. The 'loading' notification
   * repaints the cached cart (same content), the commit repaints the fresh one.
   *
   * Holds the same lock as the stepper: a stepper press mid-read would commit
   * behind this read and could then be overwritten by the older cart.
   * Becomes redundant once PLP adds go through actions.add (Phase 4/6).
   */
  refresh() {
    if (!this.blocksContainer || this.pending) return;

    this.setPending(true);
    actions
      .load()
      .catch((error) => console.warn('[cart-drawer] refresh on open failed', error))
      .finally(() => this.setPending(false));
  }

  handleClick = (event) => {

    if (
      event.target.closest(
        '[data-cart-drawer-open]'
      )
    ) {
      this.open(event);
    }

    if (
      event.target.closest(
        '[data-cart-drawer-close]'
      )
    ) {
      this.close();
    }
  }

  /**
   * Light dismiss. A press on the ::backdrop is dispatched to the dialog
   * itself, so `target === dialog` is necessary but not enough: on desktop the
   * dialog is a transparent box wider than its panels (upsell, 12px gap, main),
   * and on mobile it is the whole viewport, where a tap on the empty cart's
   * blank space also targets the dialog yet must not close anything. So the
   * backdrop is a point outside the dialog's own box. pointerdown rather than
   * click: a drag that starts on a panel and ends over the backdrop (selecting
   * text) would otherwise read as a click on the dialog and close it.
   * Primary button only — a right-click on the backdrop is not a dismissal.
   */
  handleBackdropPress = (event) => {
    if (event.button !== 0 || event.target !== this.dialog) return;

    const box = this.dialog.getBoundingClientRect();
    const inside = event.clientX >= box.left && event.clientX <= box.right
      && event.clientY >= box.top && event.clientY <= box.bottom;
    if (inside) return;

    this.close();
  }

  open(event) {
  if (event) {
    event.preventDefault();
  }

  /* reopened mid-slide-out: drop the pending close and slide back in from
     wherever the drawer currently sits */
  this.cancelPendingClose();

  /* the editor can only be reached from an open drawer, so this is the
     earliest useful moment to warm its product fetch */
  if (this.giftcardEditor) this.giftcardEditor.prefetch();

  /* re-stamp the rewards strip: the geo country may have arrived since the
     page hydrated (see paintRewards) */
  this.paintRewards(getState());

  /* the cached state paints and slides in first; the fresh cart repaints when
     it lands (see refresh) */
  this.refresh();

  document.body.classList.add('drawer-open');

  if (this.dialog.open) {
    this.classList.add('cart-drawer-modal--visible');
    return;
  }

  this.dialog.showModal();

  /* the dialog is display:none until [open], so the parked transform has never
     been computed yet — force a reflow to commit it, otherwise the browser
     coalesces "shown" and "visible" into one recalc and the slide is skipped */
  void this.dialog.offsetWidth;

  this.classList.add('cart-drawer-modal--visible');
}
  /**
   * Tear down a slide-out that is still in flight, leaving the dialog open.
   * Called both when the close lands and when open() interrupts it.
   */
  cancelPendingClose() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (this.onCloseTransitionEnd) {
      this.dialog.removeEventListener(
        'transitionend',
        this.onCloseTransitionEnd
      );
      this.onCloseTransitionEnd = null;
    }
  }

  close() {

    // an editor left open would outlive the drawer it belongs to, sitting in
    // the top layer over the page
    if (this.giftcardEditor) this.giftcardEditor.close();
    if (this.giftPicker) this.giftPicker.close();

    // a size picker left open would greet the next open with a stale pill
    const upsell = this.querySelector('cart-upsell');
    if (upsell && typeof upsell.closePicker === 'function') upsell.closePicker({ restoreFocus: false });

    document.body.classList.remove(
      'drawer-open'
    );

    if (!this.classList.contains('cart-drawer-modal--visible')) {
      this.cancelPendingClose();
      this.dialog.close();
      return;
    }

    this.classList.remove('cart-drawer-modal--visible');

    /* close once the slide-out has finished. The timeout is the safety net for
       any case where transitionend never arrives (reduced motion, the dialog
       being off-screen already, a dropped frame) — without it the dialog would
       keep its [open] attribute and stay in the top layer for good. */
    const finish = () => {
      this.cancelPendingClose();
      if (this.dialog.open) {
        this.dialog.close();
      }
    };

    /* only the dialog's own transform counts — transitionend bubbles, so a
       child animation would otherwise cut the slide-out short */
    this.onCloseTransitionEnd = (event) => {
      if (event.target === this.dialog && event.propertyName === 'transform') {
        finish();
      }
    };

    this.dialog.addEventListener(
      'transitionend',
      this.onCloseTransitionEnd
    );

    this.closeTimer = setTimeout(finish, 400);
  }
}

/**
 * A <script type="application/json"> the Liquid shell ships (strings, config).
 * A missing node or bad JSON both yield {} — the callers fall back to their
 * hard-coded English, as the data-cart-i18n path already does.
 */
// MediaQueryList change listener with the Safari < 14 spelling as fallback.
function listenMedia(query, handler, on) {
  if (typeof query.addEventListener === 'function') {
    query[on ? 'addEventListener' : 'removeEventListener']('change', handler);
  } else if (typeof query.addListener === 'function') {
    query[on ? 'addListener' : 'removeListener'](handler);
  }
}

function parseJsonNode(node, label) {
  if (!node) return {};
  try {
    return JSON.parse(node.textContent) || {};
  } catch (error) {
    console.warn(`[cart-drawer] bad ${label} JSON`, error);
    return {};
  }
}

customElements.define(
  'theme-cart-drawer',
  ThemeCartDrawer
);
